import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);
  const servicesRef = useRef(null);
  const areasRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const location = useLocation();
  const scrolledRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    // Throttled scroll handler using RAF to prevent forced reflows
    const handleScroll = () => {
      if (rafRef.current) return;
      
      rafRef.current = requestAnimationFrame(() => {
        const shouldBeScrolled = window.scrollY > 20;
        if (scrolledRef.current !== shouldBeScrolled) {
          scrolledRef.current = shouldBeScrolled;
          setIsScrolled(shouldBeScrolled);
        }
        rafRef.current = null;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsAreasOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
      if (areasRef.current && !areasRef.current.contains(event.target)) {
        setIsAreasOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Toggle mobile menu with explicit function
  const toggleMobileMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // Close mobile menu
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Nav links - Snow Removal always visible as last item before Contact
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Residential', path: '/residential' },
    { name: 'Commercial', path: '/commercial' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Snow Removal', path: '/snow-removal' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'Landscape Services', path: '/services/landscape' },
    { name: 'Landscape Design', path: '/services/landscape-design' },
    { name: 'Hardscape', path: '/services/hardscape' },
    { name: 'Lawn Maintenance', path: '/services/lawn-maintenance' },
    { name: 'Tree Service', path: '/services/tree-service' },
    { name: 'Power Sweeping', path: '/services/power-sweeping' },
  ];

  const serviceAreaLinks = [
    { name: 'New Berlin, WI', path: '/landscaping-new-berlin-wi' },
    { name: 'Brookfield, WI', path: '/landscaping-brookfield-wi' },
    { name: 'Menomonee Falls, WI', path: '/landscaping-menomonee-falls-wi' },
    { name: 'Waukesha, WI', path: '/landscaping-waukesha-wi' },
    { name: 'Muskego, WI', path: '/landscaping-muskego-wi' },
    { name: 'Milwaukee, WI', path: '/landscaping-milwaukee-wi' },
  ];

  const isActive = (path) => location.pathname === path;
  const isServiceActive = serviceLinks.some(link => location.pathname === link.path);
  const isAreaActive = serviceAreaLinks.some(link => location.pathname === link.path);

  return (
    <>
      {/* Top Bar - uses transform instead of top for smooth animation without reflow */}
      <div 
        className={`fixed top-[52px] w-full bg-primary text-white py-2 text-sm hidden md:block z-40 transition-transform duration-300 ${
          isScrolled ? '-translate-y-[92px]' : 'translate-y-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <p className="font-manrope">Serving Metro Milwaukee Since 2014 | SBE Certified</p>
          <div className="flex items-center gap-6">
            <a href="tel:414-795-3610" className="flex items-center gap-2 hover:text-accent transition-colors" data-testid="topbar-phone">
              <Phone className="w-4 h-4" />
              <span>(414) 795-3610</span>
            </a>
            <a href="mailto:info@waybetterlandandsnow.com" className="flex items-center gap-2 hover:text-accent transition-colors" data-testid="topbar-email">
              <Mail className="w-4 h-4" />
              <span>info@waybetterlandandsnow.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation - uses transform instead of top for smooth animation without reflow */}
      <nav 
        className={`fixed top-[52px] md:top-[92px] w-full z-50 transition-transform duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg -translate-y-[52px] md:-translate-y-[92px]' 
            : 'bg-white/90 backdrop-blur-sm translate-y-0'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_snow-landscape-depot/artifacts/n8qj5gl1_LOGO.png" 
                alt="WayBetter Landscaping & Snowplowing" 
                className="h-16 w-auto"
                width="128"
                height="64"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.slice(0, 1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-barlow font-semibold uppercase tracking-wide text-sm transition-colors ${
                    isActive(link.path)
                      ? 'text-primary'
                      : 'text-stone-600 hover:text-primary'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Services Dropdown */}
              <div className="relative" ref={servicesRef}>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={`flex items-center gap-1 font-barlow font-semibold uppercase tracking-wide text-sm transition-colors ${
                    isServiceActive
                      ? 'text-primary'
                      : 'text-stone-600 hover:text-primary'
                  }`}
                  data-testid="nav-services-dropdown"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isServicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-stone-200 py-2 z-50">
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-2 font-manrope text-sm transition-colors ${
                          isActive(link.path)
                            ? 'text-primary bg-primary/5'
                            : 'text-stone-600 hover:text-primary hover:bg-stone-50'
                        }`}
                        data-testid={`nav-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Service Areas Dropdown */}
              <div className="relative" ref={areasRef}>
                <button
                  onClick={() => setIsAreasOpen(!isAreasOpen)}
                  className={`flex items-center gap-1 font-barlow font-semibold uppercase tracking-wide text-sm transition-colors ${
                    isAreaActive
                      ? 'text-primary'
                      : 'text-stone-600 hover:text-primary'
                  }`}
                  data-testid="nav-areas-dropdown"
                >
                  Areas
                  <ChevronDown className={`w-4 h-4 transition-transform ${isAreasOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isAreasOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-stone-200 py-2 z-50">
                    {serviceAreaLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-2 font-manrope text-sm transition-colors ${
                          isActive(link.path)
                            ? 'text-primary bg-primary/5'
                            : 'text-stone-600 hover:text-primary hover:bg-stone-50'
                        }`}
                        data-testid={`nav-area-${link.name.toLowerCase().replace(/[\s,]+/g, '-')}`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-barlow font-semibold uppercase tracking-wide text-sm transition-colors ${
                    isActive(link.path)
                      ? 'text-primary'
                      : 'text-stone-600 hover:text-primary'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:414-795-3610" 
                className="flex items-center gap-2 text-primary font-barlow font-bold uppercase tracking-wider"
                data-testid="nav-call-btn"
              >
                <Phone className="w-5 h-5" />
                <span>Call Now</span>
              </a>
              <Link
                to="/contact"
                className="bg-primary text-white hover:bg-primary-600 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-3 px-6 text-sm"
                data-testid="nav-quote-btn"
              >
                Claim Your Free Assessment
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-stone-600 hover:text-primary active:text-primary touch-manipulation relative z-[60]"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-toggle"
              type="button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="lg:hidden fixed inset-0 bg-black/20 z-40"
              onClick={closeMobileMenu}
              data-testid="mobile-menu-backdrop"
            />
            {/* Menu Panel */}
            <div 
              ref={mobileMenuRef}
              className="lg:hidden fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white shadow-2xl animate-slide-in-right overflow-y-auto z-50"
              style={{ height: '100dvh' }}
              data-testid="mobile-menu"
            >
              {/* Close button inside menu */}
              <div className="flex items-center justify-between p-4 border-b border-stone-200 bg-white sticky top-0 z-10">
                <span className="font-barlow font-bold text-primary uppercase tracking-wider">Menu</span>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 text-stone-600 hover:text-primary touch-manipulation"
                  aria-label="Close menu"
                  type="button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="px-4 py-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={closeMobileMenu}
                    className={`block font-barlow font-semibold uppercase tracking-wide text-lg py-3 touch-manipulation ${
                      isActive(link.path)
                        ? 'text-primary'
                        : 'text-stone-600 active:text-primary'
                    }`}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
                
                {/* Mobile Services Section */}
                <div className="pt-4 border-t border-stone-200">
                  <p className="font-barlow font-bold uppercase tracking-wider text-sm text-stone-500 mb-3">Services</p>
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobileMenu}
                      className={`block font-manrope text-base py-3 pl-4 touch-manipulation ${
                        isActive(link.path)
                          ? 'text-primary'
                          : 'text-stone-600 active:text-primary'
                      }`}
                      data-testid={`mobile-nav-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile Service Areas Section */}
                <div className="pt-4 border-t border-stone-200">
                  <p className="font-barlow font-bold uppercase tracking-wider text-sm text-stone-500 mb-3">Service Areas</p>
                  {serviceAreaLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobileMenu}
                      className={`block font-manrope text-base py-3 pl-4 touch-manipulation ${
                        isActive(link.path)
                          ? 'text-primary'
                          : 'text-stone-600 active:text-primary'
                      }`}
                      data-testid={`mobile-nav-area-${link.name.toLowerCase().replace(/[\s,]+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>

                <div className="pt-4 border-t border-stone-200 space-y-4">
                  <a 
                    href="tel:414-795-3610" 
                    className="flex items-center gap-2 text-primary font-barlow font-bold uppercase tracking-wider py-2 touch-manipulation"
                    data-testid="mobile-call-btn"
                  >
                    <Phone className="w-5 h-5" />
                    <span>(414) 795-3610</span>
                  </a>
                  <Link
                    to="/contact"
                    onClick={closeMobileMenu}
                    className="block text-center bg-primary text-white hover:bg-primary-600 active:bg-primary-700 shadow-truck font-barlow font-bold uppercase tracking-wider py-4 px-6 touch-manipulation"
                    data-testid="mobile-quote-btn"
                  >
                    Claim Your Free Assessment
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>
    </>
  );
};
